import React, { useRef, useEffect } from "react";
import toast, { ToastBar, resolveValue, Toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface PausableToastBarProps {
  t: Toast; // The toast object from the render function
}

const PausableToastBar: React.FC<PausableToastBarProps> = ({ t }) => {
  const { t: translate } = useTranslation();
  const toastRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = toastRef.current;
    if (!node) return;

    const handleAnimationEnd = (event: AnimationEvent) => {
      // Ensure we're only acting on our specific progress bar animation
      // Updated to match the new keyframe name 'progress'
      if (event.animationName === "progress") {
        toast.dismiss(t.id);
      }
    };

    // The 'animationend' event fires when a CSS animation has completed.
    node.addEventListener("animationend", handleAnimationEnd);

    // Cleanup function to remove the event listener
    return () => {
      node.removeEventListener("animationend", handleAnimationEnd);
    };
  }, [t.id]); // Re-attach the listener if the toast ID changes

  // 1. Create a mutable copy of the toast object to avoid direct mutation.
  const modifiedToast = { ...t };

  // 2. Build the class names arrays for both the toast and its close button.
  const toastClasses = [modifiedToast.className || ""];
  const buttonClasses = ["app-toast__close-btn"]; // Base class for the button

  // Apply type-specific modifier classes
  switch (t.type) {
    case "success":
      toastClasses.push("app-toast--success");
      buttonClasses.push("app-toast__close-btn--success");
      break;
    case "error":
      toastClasses.push("app-toast--error");
      buttonClasses.push("app-toast__close-btn--error");
      break;
    case "loading":
      // For loading, we add no color modifier, so it uses the base transparent styles.
      break;
    default:
      // For 'blank', 'custom', etc., default to the 'info' style.
      toastClasses.push("app-toast--info");
      buttonClasses.push("app-toast__close-btn--info");
      break;
  }

  // 3. Update the className property on our *modified* toast object.
  modifiedToast.className = toastClasses.join(" ").trim();

  return (
    // Wrap ToastBar in a div to attach our ref and event listener
    <div ref={toastRef}>
      <ToastBar
        // 4. Pass the MODIFIED toast object to the `toast` prop.
        // ToastBar will internally read `modifiedToast.className`.
        toast={modifiedToast}
        // Merge the library's calculated styles (for positioning) with our custom property.
        style={
          {
            ...t.style,
            "--toast-duration": `${t.duration}ms`,
          } as React.CSSProperties
        }
      >
        {({ icon, message }) => (
          <>
            {icon}
            <div className="app-toast__message">{resolveValue(message, t)}</div>
            {t.type !== "loading" && (
              <button
                className={buttonClasses.join(" ")} // Apply dynamic classes
                onClick={() => toast.dismiss(t.id)}
                aria-label={translate("toaster.dismissLabel", "Dismiss")} // Use translated aria-label
              >
                &times;
              </button>
            )}
          </>
        )}
      </ToastBar>
    </div>
  );
};

export default PausableToastBar;
